import { useCallback, useEffect, useRef, useState } from 'react';
import { aiChatService } from '../services/AIChatService';
import { getViewContext } from '../../core/services/viewContext';
import type { ChatMessage, ChatStreamEvent, ToolCall } from '../types';

/** Folds one `tool_use`/`tool_result`/`approval_required` event into a message's `toolCalls` — mirrors the backend's own folding (`ai-chat-messages.controller.ts`'s `foldToolCallEvent`). */
function foldToolCall(toolCalls: ToolCall[], event: ChatStreamEvent): ToolCall[] {
  const toolUseId = event.toolUseId;
  if (!toolUseId) return toolCalls;
  const idx = toolCalls.findIndex((t) => t.toolUseId === toolUseId);

  if (event.kind === 'tool_use') {
    const entry: ToolCall = {
      toolUseId,
      name: event.tool?.name ?? '(unknown)',
      input: event.tool?.input,
      // The client can't know requiresApproval/executionTarget until an
      // `approval_required` event (backend lane) arrives, or the tool
      // finishes (frontend lane, e.g. recommend_action never pauses) — a
      // reconnect via GET .../messages already has the persisted, correct value.
      requiresApproval: idx >= 0 ? toolCalls[idx].requiresApproval : false,
      executionTarget: idx >= 0 ? toolCalls[idx].executionTarget : undefined,
    };
    if (idx === -1) return [...toolCalls, entry];
    const copy = [...toolCalls];
    copy[idx] = { ...copy[idx], ...entry };
    return copy;
  }

  if (idx === -1) return toolCalls;

  if (event.kind === 'tool_result') {
    const copy = [...toolCalls];
    copy[idx] = { ...copy[idx], output: event.tool?.output };
    return copy;
  }

  if (event.kind === 'approval_required') {
    const copy = [...toolCalls];
    copy[idx] = { ...copy[idx], requiresApproval: true, executionTarget: copy[idx].executionTarget ?? 'backend' };
    return copy;
  }

  return toolCalls;
}

/** Folds one stream event into the matching message — mirrors the backend's own `persistEvent` (ai-chat-messages.controller.ts). */
function applyStreamEvent(messages: ChatMessage[], messageId: number, event: ChatStreamEvent): ChatMessage[] {
  return messages.map((m) => {
    if (m.id !== messageId) return m;
    switch (event.kind) {
      case 'message':
        return event.delta ? { ...m, content: m.content + event.delta, status: 'STREAMING' } : m;
      case 'tool_use':
      case 'tool_result':
      case 'approval_required':
        return { ...m, toolCalls: foldToolCall(m.toolCalls ?? [], event) };
      case 'completed':
        return {
          ...m,
          content: typeof event.result === 'string' ? event.result : m.content,
          status: 'COMPLETED',
          citations: event.citations ?? m.citations,
          inputTokens: event.usage?.inputTokens ?? m.inputTokens,
          outputTokens: event.usage?.outputTokens ?? m.outputTokens,
        };
      case 'failed':
        return { ...m, status: 'FAILED', error: event.error ?? 'Something went wrong' };
      case 'cancelled':
        return { ...m, status: 'CANCELLED' };
      default:
        return m;
    }
  });
}

/**
 * Drives the message list for one session: loads history, sends new
 * messages, attaches the SSE stream for whichever assistant reply is
 * in flight, and exposes cancel/regenerate/approve/reject. One instance per
 * active session — `AIChatSidebar` re-mounts it (via `key`) when the
 * session changes, so there's no cross-session state to reconcile here.
 */
export function useAIChat(sessionId: number | null, onTitleGenerated?: (sessionId: number, title: string) => void) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [approachingLimit, setApproachingLimit] = useState(false);
  const streamAbortRef = useRef<AbortController | null>(null);

  const attachStream = useCallback((assistantMessageId: number) => {
    streamAbortRef.current?.abort();
    const controller = new AbortController();
    streamAbortRef.current = controller;
    setSending(true);

    aiChatService
      .streamMessage(
        assistantMessageId,
        (event) => setMessages((prev) => applyStreamEvent(prev, assistantMessageId, event)),
        controller.signal,
      )
      .catch(() => {
        // A dropped connection (network blip, tab backgrounded) doesn't lose
        // the reply — the backend keeps generating and persisting it; the
        // next load of this session picks up wherever it landed.
      })
      .finally(() => {
        if (streamAbortRef.current === controller) setSending(false);
      });
  }, []);

  useEffect(() => {
    streamAbortRef.current?.abort();
    if (sessionId == null) {
      setMessages([]);
      setApproachingLimit(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    aiChatService
      .listMessages(sessionId)
      .then((history) => {
        if (cancelled) return;
        setMessages(history);
        const last = history[history.length - 1];
        if (last && last.role === 'ASSISTANT' && last.status === 'STREAMING') {
          attachStream(last.id);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, attachStream]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (sessionId == null) return;
      setSending(true);
      const {
        userMessageId,
        assistantMessageId,
        approachingLimit: limit,
        title,
      } = await aiChatService.sendMessage(sessionId, content, getViewContext());
      setApproachingLimit(limit);
      if (title) onTitleGenerated?.(sessionId, title);
      const now = new Date().toISOString();
      setMessages((prev) => [
        ...prev,
        {
          id: userMessageId,
          sessionId,
          role: 'USER',
          content,
          status: 'COMPLETED',
          inputTokens: null,
          outputTokens: null,
          error: null,
          createdAt: now,
        },
        {
          id: assistantMessageId,
          sessionId,
          role: 'ASSISTANT',
          content: '',
          status: 'STREAMING',
          inputTokens: null,
          outputTokens: null,
          error: null,
          createdAt: now,
        },
      ]);
      attachStream(assistantMessageId);
    },
    [sessionId, attachStream, onTitleGenerated],
  );

  const cancel = useCallback(async () => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'ASSISTANT') return;
    streamAbortRef.current?.abort();
    setSending(false);
    await aiChatService.cancelMessage(last.id);
    setMessages((prev) => prev.map((m) => (m.id === last.id ? { ...m, status: 'CANCELLED' } : m)));
  }, [messages]);

  const regenerate = useCallback(async () => {
    const lastAssistant = [...messages].reverse().find((m) => m.role === 'ASSISTANT');
    if (!lastAssistant || sessionId == null) return;
    const { assistantMessageId, approachingLimit: limit } = await aiChatService.regenerateMessage(lastAssistant.id);
    setApproachingLimit(limit);
    setMessages((prev) => [
      ...prev,
      {
        id: assistantMessageId,
        sessionId,
        role: 'ASSISTANT',
        content: '',
        status: 'STREAMING',
        inputTokens: null,
        outputTokens: null,
        error: null,
        createdAt: new Date().toISOString(),
      },
    ]);
    attachStream(assistantMessageId);
  }, [messages, sessionId, attachStream]);

  const setToolCallDecision = useCallback((messageId: number, toolUseId: string, decision: 'approved' | 'rejected') => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id !== messageId
          ? m
          : {
              ...m,
              toolCalls: (m.toolCalls ?? []).map((t) => (t.toolUseId === toolUseId ? { ...t, decision } : t)),
            },
      ),
    );
  }, []);

  /** Backend-executed lane (§6.1) — resumes the paused job; the tool then actually runs and a `tool_result` event follows on the open stream. */
  const approveToolCall = useCallback(
    async (messageId: number, toolUseId: string) => {
      setToolCallDecision(messageId, toolUseId, 'approved');
      await aiChatService.approveToolCall(messageId, toolUseId);
    },
    [setToolCallDecision],
  );

  const rejectToolCall = useCallback(
    async (messageId: number, toolUseId: string) => {
      setToolCallDecision(messageId, toolUseId, 'rejected');
      await aiChatService.rejectToolCall(messageId, toolUseId);
    },
    [setToolCallDecision],
  );

  /** Frontend-executed lane (§6.2) — the caller has already performed (or dismissed) the recommended action via its own existing controls; this only records the decision for history. */
  const decideToolCall = useCallback(
    async (messageId: number, toolUseId: string, decision: 'approved' | 'rejected') => {
      setToolCallDecision(messageId, toolUseId, decision);
      await aiChatService.decideToolCall(messageId, toolUseId, decision);
    },
    [setToolCallDecision],
  );

  return {
    messages,
    loading,
    sending,
    approachingLimit,
    sendMessage,
    cancel,
    regenerate,
    approveToolCall,
    rejectToolCall,
    decideToolCall,
  };
}
